export {
  whenReady,
  MaybeBool,
  observeDom,
  fireEvent,
  apply,
  KnownKeysMatching,
  KeysMatching,
  KnownKeys,
  eventAssigner,
  Kinded,
  getApiUrl,
  getBaseUrl,
  getOS,
}

/**
 * Get a normalized OS name: "Mac OS", "iOS", "Windows", "Android", "Linux", or null.
 */
function getOS() {
  //@ts-ignore
  const { userAgentData, platform: legacyPlatform, userAgent } = window.navigator;

  // Try Client Hints API first (Chromium-based: Chrome, Edge, Opera)
  if (userAgentData?.platform) {
    const plat = userAgentData.platform;
    if (/^Mac/.test(plat)) return 'Mac OS';
    if (/^Windows/.test(plat)) return 'Windows';
    if (/^Android/.test(plat)) return 'Android';
    if (/^iPhone|iPad|iPod/.test(plat)) return 'iOS';
    if (/^Linux/.test(plat)) return 'Linux';
  }

  // Fallback to navigator.platform (deprecated but still supported elsewhere)
  const platform = legacyPlatform?.toLowerCase() || '';
  if (platform.startsWith('mac')) return 'Mac OS';
  if (platform.startsWith('win')) return 'Windows';
  if (platform.startsWith('linux')) return 'Linux';
  if (/iphone|ipad|ipod/.test(platform)) return 'iOS';

  // Final fallback: parse userAgent string
  if (/android/.test(userAgent)) return 'Android';
  if (/ipad|iphone|ipod/.test(userAgent)) return 'iOS';
  if (/mac os x/.test(userAgent)) return 'Mac OS';
  if (/windows|win32/.test(userAgent)) return 'Windows';
  if (/linux/.test(userAgent)) return 'Linux';

  // Couldn’t confidently detect
  return null;
}

export function debounce(func: () => any, wait: number, immediate?: boolean): () => void
export function debounce<T1>(func: (a: T1) => any, wait: number, immediate?: boolean): (a: T1) => void
export function debounce<T1, T2>(func: (a: T1, b: T2) => any, wait: number, immediate?: boolean): (a: T1, b: T2) => void
export function debounce<T1, T2, T3>(func: (a: T1, b: T2, c: T3) => any, wait: number, immediate?: boolean): (a: T1, b: T2, c: T3) => void
export function debounce<T1, T2, T3, T4>(func: (a1: T1, a2: T2, a3: T3, a4: T4) => any, wait: number, immediate?: boolean): (a1: T1, a2: T2, a3: T3, a4: T4) => void
export function debounce<T1, T2, T3, T4, T5>(func: (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5) => any, wait: number, immediate?: boolean): (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5) => void
export function debounce<T1, T2, T3, T4, T5, T6>(func: (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5, a6: T6) => any, wait: number, immediate?: boolean): (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5, a6: T6) => void
export function debounce<T1, T2, T3, T4, T5, T6, T7>(func: (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5, a6: T6, a7: T7) => any, wait: number, immediate?: boolean): (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5, a6: T6, a7: T7) => void
export function debounce<T1, T2, T3, T4, T5, T6, T7, T8>(func: (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5, a6: T6, a7: T7, a8: T8) => any, wait: number, immediate?: boolean): (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5, a6: T6, a7: T7, a8: T8) => void {
  let timeout: any;
  return function () {
    // @ts-ignore
    let context: any = this, args: any = arguments;
    let later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    let callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

function log(msg: string, ...rest: any[]): <val>(a: val) => val {
  return a => {
    console.log(msg, ...rest, a);
    return a;
  };
}

function logErr<val>(msg: string, ...rest: any[]): (a: val) => val {
  return a => {
    console.error(msg, ...rest, a);
    return a;
  };
}

function browserLocale(): string {
  let lang;

  if (navigator.languages && navigator.languages.length) {
    // latest versions of Chrome and Firefox set this correctly
    lang = navigator.languages[0];
  } else if ((<any>navigator).userLanguage) {
    // IE only
    lang = (<any>navigator).userLanguage;
  } else {
    // latest versions of Chrome, Firefox, and Safari set this correctly
    lang = navigator.language;
  }

  return lang;
}

const apply =
  <I>(value: I) =>
    <O>(fn: (i: I) => O): O =>
      fn(value);

type MaybeBool = true | false | 'UNKNOWN';

type MutationObserverDefaults<T extends MutationObserverInit> =
  (T["attributes"] extends true ? { attributes: true } : T["attributeOldValue"] extends true ? { attributes: true } : T["attributeFilter"] extends string[] ? { attributes: true } : {})
  & (T["characterData"] extends true ? { characterData: true } : T["characterDataOldValue"] extends true ? { characterData: true } : {})
  & (T["childList"] extends true ? { childList: true } : {})

type MutationCallbackRecord<N extends Node, T extends Partial<Record<MutationRecordType, true>>> =
  Record<[keyof T] extends [never] ? "onChildList" : `on${Capitalize<string & keyof T>}`, (this: N, mutation: MutationRecord) => void>

function observeDom<N extends Node, T extends MutationObserverInit>(
  target: N,
  options: T,
  callbacks: MutationCallbackRecord<N, MutationObserverDefaults<T>>
): MutationObserver {
  const _options = toOptions(options);
  const observer = new MutationObserver(_callback);
  observer.observe(target, _options);
  return observer;

  function _callback(mutations: MutationRecord[], _observer: MutationObserver): void {
    mutations.forEach(mutation => {
      const type = `on${mutation.type[0].toUpperCase()}${mutation.type.slice(1)}` as keyof typeof callbacks;
      callbacks[type].bind(target)(mutation);
    });
  }

  function toOptions(options: MutationObserverInit): MutationObserverInit {
    const hasMutation = Object.keys(options).some(key => key !== "subtree");
    return hasMutation ? options : { childList: true };
  }
}


const fireEvent = (node: any, evtName: any) => {
  if (CustomEvent) {
    const evt = new CustomEvent(evtName, { bubbles: true, cancelable: true });
    node.dispatchEvent(evt);
  } else if (document.createEvent) {
    const evt = document.createEvent('MouseEvents');
    evt.initEvent(evtName, true, false);
    node.dispatchEvent(evt);
  } else if ((<any>document).createEventObject) {
    node.fireEvent(evtName);
  } else {
    throw new Error('No way to trigger markerTap event');
  }
};

// https://stackoverflow.com/a/58980331/592641
type KnownKeysMatching<T, V> = KeysMatching<Pick<T, KnownKeys<T>>, V>
type KeysMatching<T, V> = { [K in keyof T]: T[K] extends V ? K : never }[keyof T];
type KnownKeys<T> = Extract<{
  [K in keyof T]: string extends K ? never : number extends K ? never : K
} extends { [_ in keyof T]: infer U } ? U : never, keyof T>;


const eventAssigner = <
  M extends Record<keyof M, (e: any) => any>,
  D extends keyof any
>(
  handlers: M,
  discriminant: D
) => <K extends keyof M>(
  event: Record<D, K> & (Parameters<M[K]>[0])
): ReturnType<M[K]> => handlers[event[discriminant]](event);

type Kinded<EventMap extends object, Discriminant extends string> = { [key in keyof EventMap]: Record<Discriminant, key> & EventMap[key] }[keyof EventMap]

const whenReady: Promise<void> =
    new Promise(resolve =>
        (document.addEventListener('DOMContentLoaded', () => resolve(), false)));

function getApiUrl(): string {
  const config = process.env.config ? process.env.config : Object.create(null);
  return config.apiUrl ? config.apiUrl : ''
}

function getBaseUrl(): string {
  const config = process.env.config ? process.env.config : Object.create(null);

  return window.location.origin + '/';
  //config.appUrl ? config.appUrl + '/' : ''
}